import { useEffect, useRef, useState } from 'react'
import { photos, type PhotoKind, type PhotoRef } from '../lib/photos'

/**
 * Take a photograph.
 *
 * This replaces a button that toggled a boolean and wrote the string
 * `photo://<uuid>` into the record, which the console then drew a "photo attached"
 * chip from. A record claiming evidence that does not exist is worse than no record,
 * because somebody relies on it in an argument they then lose.
 *
 * `capture="environment"` asks the phone for the back camera directly rather than the
 * gallery, which is what somebody standing at a scale wants.
 */
export function PhotoButton({
  kind, where, value, onChange, label, required,
}: {
  kind: PhotoKind
  /** Stamped onto the photo itself, so the picture carries its own place and time. */
  where: { lat: number; lng: number; accuracy: number } | null
  value: PhotoRef | null
  onChange: (ref: PhotoRef | null) => void
  label?: string
  required?: boolean
}) {
  const input = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [waiting, setWaiting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function take(file: File) {
    setBusy(true)
    setError(null)
    try {
      const res = await photos.capture(file, kind, where)
      setPreview(res.preview)
      setWaiting(!res.uploaded)
      onChange(res.ref)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not use that photo.')
    } finally {
      setBusy(false)
    }
  }

  function clear() {
    if (preview) URL.revokeObjectURL(preview)
    setPreview(null)
    setWaiting(false)
    onChange(null)
  }

  return (
    <div className="photo-field">
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void take(f)
          e.target.value = ''
        }}
      />

      {preview ? (
        <div className="photo-taken">
          <img src={preview} alt="" />
          <div className="photo-meta">
            <b>{waiting ? 'Saved on this phone' : 'Photo attached'}</b>
            <span>
              {waiting
                ? 'It sends itself when you have signal.'
                : where
                  ? `Where and when it was taken is on it.`
                  : 'No GPS on it — no position was available.'}
            </span>
          </div>
          <button type="button" className="photo-drop" onClick={clear} aria-label="Remove photo">
            ✕
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={`btn ${required ? '' : 'ghost'} block`}
          disabled={busy}
          onClick={() => input.current?.click()}
        >
          {busy ? 'Saving the photo…' : (label ?? 'Take a photo')}
          {required && !value && <span className="crit-t"> *</span>}
        </button>
      )}

      {error && <div className="error-text">{error}</div>}
    </div>
  )
}

/**
 * A stored photo, shown wherever the evidence is being looked at. Needs a signed-in
 * session, so it goes through fetch rather than a bare img src.
 */
export function Photo({ refValue, alt = '' }: { refValue: PhotoRef | null; alt?: string }) {
  const [src, setSrc] = useState<string | null>(null)
  const [gone, setGone] = useState(false)

  useEffect(() => {
    let url: string | null = null
    let cancelled = false

    void (async () => {
      const local = await photos.localPreview(refValue)
      const found = local ?? (await photos.fetchBlobUrl(refValue))
      if (cancelled) {
        if (found) URL.revokeObjectURL(found)
        return
      }
      if (found) { url = found; setSrc(found) } else setGone(true)
    })()

    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [refValue])

  if (!refValue) return null
  if (gone) return <span className="chip off">▣ photo missing</span>
  if (!src) return <span className="chip">▣ loading…</span>

  return <img className="photo-thumb" src={src} alt={alt} />
}
